// react-native-image-picker stand-in backed by a hidden <input type="file">.
// Returns the same `{ assets: [{ uri, fileName, fileSize, type }] }` shape
// the app's screens read. `launchCamera` opens the same picker with the
// `capture` hint so phones offer the camera.

export interface Asset {
  uri: string;
  fileName?: string;
  fileSize?: number;
  type?: string;
  width?: number;
  height?: number;
  base64?: string;
  file?: File;
}

export interface ImagePickerResponse {
  didCancel?: boolean;
  errorCode?: string;
  errorMessage?: string;
  assets?: Asset[];
}

export interface Options {
  mediaType?: 'photo' | 'video' | 'mixed';
  includeBase64?: boolean;
  selectionLimit?: number;
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
  cameraType?: 'back' | 'front';
}

type Callback = (response: ImagePickerResponse) => void;

function acceptFor(mediaType: Options['mediaType']): string {
  if (mediaType === 'video') return 'video/*';
  if (mediaType === 'mixed') return 'image/*,video/*';
  return 'image/*';
}

async function toAsset(file: File, includeBase64: boolean): Promise<Asset> {
  const asset: Asset = {
    uri: URL.createObjectURL(file),
    fileName: file.name,
    fileSize: file.size,
    type: file.type,
    file,
  };
  if (includeBase64) {
    asset.base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }
  return asset;
}

function pick(options: Options = {}, capture?: 'user' | 'environment'): Promise<ImagePickerResponse> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptFor(options.mediaType);
    input.multiple = (options.selectionLimit ?? 1) !== 1;
    if (capture) input.setAttribute('capture', capture);
    input.style.display = 'none';

    let settled = false;
    const finish = (response: ImagePickerResponse) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(response);
    };

    input.addEventListener('change', async () => {
      const files = Array.from(input.files ?? []);
      if (files.length === 0) return finish({ didCancel: true });
      try {
        const assets = await Promise.all(files.map((f) => toAsset(f, !!options.includeBase64)));
        finish({ assets });
      } catch (err) {
        finish({ errorCode: 'others', errorMessage: (err as Error).message });
      }
    });
    // Browsers fire `cancel` on the input when the dialog is dismissed.
    input.addEventListener('cancel', () => finish({ didCancel: true }));

    document.body.appendChild(input);
    input.click();
  });
}

function withCallback(
  promise: Promise<ImagePickerResponse>,
  callback?: Callback,
): Promise<ImagePickerResponse> {
  if (callback) promise.then(callback);
  return promise;
}

export function launchImageLibrary(options?: Options, callback?: Callback): Promise<ImagePickerResponse> {
  return withCallback(pick(options), callback);
}

export function launchCamera(options?: Options, callback?: Callback): Promise<ImagePickerResponse> {
  const facing = options?.cameraType === 'front' ? 'user' : 'environment';
  return withCallback(pick(options, facing), callback);
}

export default { launchImageLibrary, launchCamera };
